import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Parse from 'html-react-parser';
import classnames from 'classnames';
import _ from 'lodash';
import {
  FormFeedback,
  FormGroup,
  Input,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';

import { slugify } from '../../helpers';
import { findPathToKey } from '../../helpers/App';

const DownloadReasonsElement = ({
  element,
  depth,
  path,
  handleChange,
  answerTree,
  showAnswerTreeErrors,
}) => {
  const intl = useIntl();

  const hasSelectNodes = element.nodes.find(n => n.type === 'select');

  const parentPath = path.slice(0, -1).join('_');
  const pathValue = path.join('_');
  const usePath = hasSelectNodes ? path : parentPath;

  const elementObject = _.get(answerTree, findPathToKey(answerTree, usePath));
  const elementAnswer = hasSelectNodes && elementObject ?
    elementObject?.answers[0] :
    elementObject?.answers?.find(a => a.id === pathValue);

  const showError = showAnswerTreeErrors && !!(
    _.get(answerTree, `${path.join('_')}.requiredError`) ||
    (elementObject && elementObject.nodes.find(n => Object.keys(n).find(k => n[k].requiredError)))
  );

  const isSelected = !!elementAnswer;

  return (
    <FormGroup
      className={classnames(
        'reason-form-label-group',
        {
          'reason-form-label-group-question': element.type === 'question',
          'reason-form-label-group-answer': element.type !== 'question',
          'reason-form-label-group-first': depth === 1,
          'is-invalid': showError,
        },
      )}
    >
      {element.type === 'question' && (
        <>
          <Label className={classnames(
            'reason-form-label-question',
            {
              'mb-0': showAnswerTreeErrors,
              'mb-1': showAnswerTreeErrors,
            },
          )}
          >
            {Parse(element.label)}
          </Label>
          {element.description && (
            <div className="reason-form-label-description">
              {Parse(element.description)}
            </div>
          )}
          {element.required && (
            <span
              className="required-asterisk"
              id={`download-reasons-question-required-asterisk-${path.join('_')}`}
            >
              *
              <UncontrolledTooltip
                fade={false}
                placement="bottom"
                target={`download-reasons-question-required-asterisk-${path.join('_')}`}
              >
                {intl.formatMessage({ defaultMessage: 'Required' })}
              </UncontrolledTooltip>
            </span>
          )}
          {showError && (
            <FormFeedback style={{ display: 'block' }} className="mt-0 mb-1">
              {intl.formatMessage({ defaultMessage: 'This field is required' })}
            </FormFeedback>
          )}
        </>
      )}
      {element.type === 'checkbox' && (
        <>
          <Label check>
            <Input
              type="checkbox"
              className="input-check check-s form-check-input"
              onChange={(e) => handleChange(path.join('_'), element.label, element.type, e.target.checked)}
              checked={isSelected}
            />
            {' '}
            {Parse(element.label)}
          </Label>
          {element.description && (
            <div className="reason-form-label-description">
              {Parse(element.description)}
            </div>
          )}
          {(isSelected && element.text) && (
            <Input
              type="text"
              name={`${path.slice(0, -1).join('_')}_text`}
              onChange={(e) => handleChange(path.join('_'), e.target.value, `${element.type}-text`, true, element.label)}
            />
          )}
        </>
      )}
      {element.type === 'radio' && (
        <>
          <Label check>
            <Input
              type="radio"
              name={`${path.slice(0, -1).join('_')}_text`}
              className="input-radio"
              onChange={(e) => handleChange(path.join('_'), element.label, element.type, e.target.checked)}
              checked={isSelected}
            />
            {' '}
            {Parse(element.label)}
          </Label>
          {element.description && (
            <div className="reason-form-label-description">
              {Parse(element.description)}
            </div>
          )}
          {(isSelected && element.text) && (
            <Input
              type="text"
              name={`${path.slice(0, -1).join('_')}_text`}
              onChange={(e) => handleChange(path.join('_'), e.target.value, `${element.type}-text`, true, element.label)}
            />
          )}
        </>
      )}
      {hasSelectNodes && (
        <Input
          type="select"
          className="custom-select"
          onChange={e => {
            const selectNodes = element.nodes.filter(n => n.type === 'select');
            const selectedNode = selectNodes[e.target.selectedIndex - 1];
            const originalIndex = element.nodes.indexOf(selectedNode);

            handleChange(
              [...path, originalIndex].join('_'),
              e.target.selectedIndex === 0 ? null : e.target.options[e.target.selectedIndex].text,
              'select',
              true,
              '',
            );
          }}
          value={elementAnswer?.value ? slugify(elementAnswer.value) : slugify(element.label[0])}
        >
          <option value={null}>{intl.formatMessage({ defaultMessage: 'Select option' })}</option>
          {element.nodes?.filter(n => n.type === 'select').map((n) => (
            <option key={slugify(n.label)} value={slugify(n.label)}>{n.label}</option>
          ))}
        </Input>
      )}
      {element.type === 'text' && (
        <Label className="reason-form-label-text">
          {element.label}
          {element.required && (
            <span
              className="required-asterisk"
              id={`download-reasons-text-required-asterisk-${path.join('_')}`}
            >
              *
              <UncontrolledTooltip
                fade={false}
                placement="bottom"
                target={`download-reasons-text-required-asterisk-${path.join('_')}`}
              >
                {intl.formatMessage({ defaultMessage: 'Required' })}
              </UncontrolledTooltip>
            </span>
          )}
          <Input
            type="text"
            onChange={(e) => handleChange(path.join('_'), e.target.value, element.type, true, element.label)}
            value={elementAnswer?.value || ''}
          />
        </Label>
      )}
      {element.nodes &&
        (
          depth === 0 ||
          element.type === 'question' ||
          isSelected
        ) &&
        element.nodes.map((n, i) => (
          <DownloadReasonsElement
            key={[...path, i].join('_')}
            element={n}
            depth={depth + 1}
            path={[...path, i]}
            handleChange={handleChange}
            answerTree={answerTree}
            showAnswerTreeErrors={showAnswerTreeErrors}
          />
        ))}
    </FormGroup>
  );
};

DownloadReasonsElement.propTypes = {
  handleChange: PropTypes.func.isRequired,
  element: PropTypes.object.isRequired,
  answerTree: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  path: PropTypes.array.isRequired,
  showAnswerTreeErrors: PropTypes.bool.isRequired,
};

export default DownloadReasonsElement;
